import { useId, useMemo, useState } from 'react';

/**
 * Graphe d'aire **mono-série** sur une série journalière (ADR-0025/0026). Une mesure, une échelle,
 * pas de légende (le titre nomme la série) ; la couleur ne fait qu'identifier. Générique : consommation
 * (jetons, transfert) et analytique (utilisateurs actifs) le partagent — une seule implémentation.
 */

/** Géométrie du tracé (unités du `viewBox`, mises à l'échelle en largeur par le CSS). */
const W = 640;
const H = 200;
const PAD = { top: 16, right: 16, bottom: 28, left: 56 } as const;
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

/** Un point de la série : jour UTC `YYYY-MM-DD` et valeur. */
export interface DayPoint {
  date: string;
  value: number;
}

interface DayAreaChartProps {
  title: string;
  points: DayPoint[];
  /** Formate une valeur pour l'axe et l'infobulle. */
  format: (value: number) => string;
  /** Suffixe d'identité pour les classes de couleur : `tokens`, `transfer` ou `users`. */
  accent: 'tokens' | 'transfer' | 'users';
  /** Libellé du total affiché en tête (défaut : « … au total »). */
  totalLabel?: (total: number) => string;
}

/** Arrondit un maximum vers une borne « ronde » pour des graduations lisibles. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

/** Jour UTC `YYYY-MM-DD` → libellé court `JJ/MM` (axe et infobulle). */
function shortDate(iso: string): string {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
}

export function DayAreaChart({ title, points, format, accent, totalLabel }: DayAreaChartProps) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const series = useMemo(() => points.map((p) => p.value), [points]);
  const peak = useMemo(() => Math.max(0, ...series), [series]);
  const maxY = niceMax(peak);
  const total = useMemo(() => series.reduce((sum, v) => sum + v, 0), [series]);

  const n = series.length;
  // Un seul point n'a pas de « ligne » : on le centre pour éviter une division par zéro.
  const xAt = (i: number) => (n <= 1 ? PAD.left + PLOT_W / 2 : PAD.left + (i / (n - 1)) * PLOT_W);
  const yAt = (v: number) => PAD.top + PLOT_H * (1 - v / maxY);

  const linePath = series.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${yAt(v)}`).join(' ');
  const areaPath =
    n > 0
      ? `M${xAt(0)},${PAD.top + PLOT_H} ` +
        series.map((v, i) => `L${xAt(i)},${yAt(v)}`).join(' ') +
        ` L${xAt(n - 1)},${PAD.top + PLOT_H} Z`
      : '';

  // Graduations Y : 0, milieu, haut — récessives, la valeur prime sur la grille.
  const ticks = [0, maxY / 2, maxY];

  const empty = n === 0 || total === 0;

  const onMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (n === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - rect.left) / rect.width;
    const index = Math.round(fraction * (n - 1));
    setHover(Math.min(Math.max(index, 0), n - 1));
  };

  const hovered = hover !== null ? points[hover] : null;

  return (
    <figure className={`ddp-chart ddp-chart--${accent}`}>
      <figcaption className="ddp-chart__caption">
        <span className="ddp-chart__title">{title}</span>
        <span className="ddp-chart__total">
          {totalLabel ? totalLabel(total) : `${format(total)} au total`}
        </span>
      </figcaption>

      <div className="ddp-chart__plot" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="ddp-chart__svg"
          role="img"
          aria-label={`${title} : pic à ${format(peak)} sur ${n} jours.`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="ddp-chart__fill-top" />
              <stop offset="100%" className="ddp-chart__fill-bottom" />
            </linearGradient>
          </defs>

          {ticks.map((t) => (
            <g key={t}>
              <line
                className="ddp-chart__grid"
                x1={PAD.left}
                x2={W - PAD.right}
                y1={yAt(t)}
                y2={yAt(t)}
              />
              <text className="ddp-chart__axis" x={PAD.left - 8} y={yAt(t)} dy="0.32em">
                {format(t)}
              </text>
            </g>
          ))}

          {!empty && <path className="ddp-chart__area" d={areaPath} fill={`url(#${gradientId})`} />}
          {!empty && n > 1 && <path className="ddp-chart__line" d={linePath} />}
          {!empty && n === 1 && (
            <circle className="ddp-chart__dot" cx={xAt(0)} cy={yAt(series[0]!)} r={4} />
          )}

          {hover !== null && !empty && (
            <g aria-hidden="true">
              <line
                className="ddp-chart__crosshair"
                x1={xAt(hover)}
                x2={xAt(hover)}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
              />
              <circle className="ddp-chart__dot" cx={xAt(hover)} cy={yAt(series[hover]!)} r={4} />
            </g>
          )}

          {n > 0 && (
            <>
              <text className="ddp-chart__axis ddp-chart__axis--x" x={xAt(0)} y={H - 8}>
                {shortDate(points[0]!.date)}
              </text>
              <text
                className="ddp-chart__axis ddp-chart__axis--x"
                x={xAt(n - 1)}
                y={H - 8}
                textAnchor="end"
              >
                {shortDate(points[n - 1]!.date)}
              </text>
            </>
          )}
        </svg>

        {hovered && (
          <div
            className="ddp-chart__tooltip"
            style={{ left: `${(xAt(hover!) / W) * 100}%` }}
            role="status"
          >
            <span className="ddp-chart__tooltip-date">{shortDate(hovered.date)}</span>
            <span className="ddp-chart__tooltip-value">{format(hovered.value)}</span>
          </div>
        )}

        {empty && <p className="ddp-chart__empty">Aucune donnée sur la période.</p>}
      </div>

      {/* Repli accessible et exact : la même donnée, lisible sans le graphe (revue dataviz). */}
      {!empty && (
        <details className="ddp-chart__data">
          <summary>Voir les valeurs</summary>
          <div className="ddp-admin__table-wrap">
            <table className="ddp-admin__table">
              <thead>
                <tr>
                  <th scope="col">Jour</th>
                  <th scope="col" className="ddp-admin__num">
                    {title}
                  </th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.date}>
                    <td>{shortDate(p.date)}</td>
                    <td className="ddp-admin__num">{format(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </figure>
  );
}
